/**
 * Shared node type constants for CloudSam Survey Designer
 * 
 * This centralizes node type definitions as recommended in:
 * docs/cloudsam_filepicker.md#Schema additions
 */

// Core node types enum
export const NODE_TYPES = {
  START: 'start',
  END: 'end',
  QUESTION: 'question',
  PACKAGE: 'package',
  MEDIA: 'media',
  DECISION: 'decision',
  PLEASANT: 'pleasant',

};

// Default data for each node type
export const NODE_DEFAULTS = {
  [NODE_TYPES.START]: {
    question_id: "START",
    question_text: "",
    question_type: "introduction",
    variable_name: null,
    required: false,
    category: "system",
    conversational: {
      sam_persona: {
        tone: "friendly",
        verbosity: "moderate",
        acknowledgment_style: "warm"
      },
      response_requirements: {
        acknowledge_input: false,
        provide_encouragement: true,
        explain_next_steps: true
      },
      conversation_management: {
        allow_tangents: false,
        redirect_method: "gentle",
        clarification_style: "guided"
      }
    },
    gravity: {
      strength: "low",
      anxiety_thresholds: {
        low: 120000,
        high: 300000,
        critical: 600000
      },
      breadcrumb_depth: 1,
      drift_tolerance: "flexible",
      redirection_style: "gentle"
    },
    analysis: {
      relevance_criteria: {
        must_contain_answer: false,
        emotional_content_acceptable: true,
        tangent_tolerance: "high"
      },
      completion_criteria: {
        minimum_confidence: 1.0,
        require_explicit_answer: false,
        accept_implied_answers: true
      },
      drift_detection: {
        topic_drift_sensitivity: "low",
        emotional_avoidance_detection: false,
        obfuscation_detection: false
      }
    }
  },
  [NODE_TYPES.END]: {
    // v5: do not hardcode "END". ID is assigned per-node (e.g., end_<timestamp>) where nodes are created.
    question_id: "", // assigned at creation time (e.g., end_<ts>)
    question_text: "",
    question_type: "completion",
    variable_name: null,
    required: false,
    category: "system",
    conversational: {
      sam_persona: {
        tone: "friendly",
        verbosity: "moderate",
        acknowledgment_style: "warm"
      },
      response_requirements: {
        acknowledge_input: false,
        provide_encouragement: true,
        explain_next_steps: true
      },
      conversation_management: {
        allow_tangents: false,
        redirect_method: "gentle",
        clarification_style: "guided"
      }
    },
    gravity: {
      strength: "low",
      anxiety_thresholds: {
        low: 120000,
        high: 300000,
        critical: 600000
      },
      breadcrumb_depth: 1,
      drift_tolerance: "flexible",
      redirection_style: "gentle"
    },
    analysis: {
      relevance_criteria: {
        must_contain_answer: false,
        emotional_content_acceptable: true,
        tangent_tolerance: "high"
      },
      completion_criteria: {
        minimum_confidence: 1.0,
        require_explicit_answer: false,
        accept_implied_answers: true
      },
      drift_detection: {
        topic_drift_sensitivity: "low",
        emotional_avoidance_detection: false,
        obfuscation_detection: false
      }
    }
  },
  [NODE_TYPES.QUESTION]: {
    id: 'Q1',
    text: 'New Question',
    type: 'text',
    variable: 'q_1',
    required: true,
    options: [],
    clarificationPolicy: 'auto',
    question_mode: 'verbatim',   // 'verbatim' = ask question as written; 'intent' = AI steers conversation to collect data
    intent_brief: '',            // optional: additional guidance for intent mode (if empty, question text serves as brief)
    // File type specific defaults (when type === 'file')
    title: 'Upload your file',
    maxSizeMB: 25,
    accept: ['.pdf', '.docx', '.txt'],
    storage: 'auto',
    quarantine: true,
    // Data capture defaults
    dataCapture: {
      level: 'formal_only',
      includeTimestamps: false,
      includeAnalysisTags: false
    }
  },
  [NODE_TYPES.PACKAGE]: {
    title: 'New Package',
    description: 'Package description'
  },

  [NODE_TYPES.MEDIA]: {
    // Media show/hide pair (beta scope: image only)
    type: 'media',
    collectsInput: false,
    show: true,
    stimulus_id: '',
    media: { src: '' },
    alt: ''
  },

  [NODE_TYPES.PLEASANT]: {
    // Non-input node whose line is written by the LLM at runtime, not authored.
    // The content guard lives in the kernel prompt builder and is deliberately
    // not exposed here — see docs/system documentation library/nodal_types.md.
    type: 'pleasant',
    collectsInput: false,
    id: '',
    length: 'one_line',
    scope: 'in_context',
    flavor: 'joke'
  },

  [NODE_TYPES.DECISION]: {
    question_type: 'decision',
    collectsInput: false,
    variable_name: null,
    required: false,
    category: 'routing',
    branches: [
      {
        branch_id: 'branch_default',
        label: 'Default',
        commentary_text: '',
        predicate: null,
        target: null
      }
    ]
  },

};

export default NODE_TYPES;